import { useSelector } from 'react-redux'
import { useState, useRef, useEffect } from 'react'
import LineChart from './charts/line/LineChart'
import {
  AxisValues,
  Breakpoint,
  chartTypes,
  disaggregations,
} from '../common/utils/enum'
import { useShowMore } from '../common/hooks/useShowMore'
import { PDFDocument } from 'pdf-lib'
import {
  BAR_CHART_OPTIONS,
  LINE_CHART_OPTIONS,
  NONE,
  SCATTER_CHART_OPTIONS,
  CORRELATION_NONE_INDICATOR_VALUE,
} from '../common/utils/constants'
import BarChart from './charts/bar/BarChart'
import DataTable from './DataTable'
import ChartCardContentLoader from '../common/components/CardContentLoader/ChartCardContentLoader'
import WorldMapWrapper from './WorldMapWrapper'
import Beeswarm from './charts/correlation/beeswarm/Beeswarm'
import { generateBarChartOptions } from './charts/bar/barChartOptions'
import ScatterChart from './charts/correlation/scatter/ScatterChart'
import { generateLineChartOptions } from './charts/line/lineChartOptions'
import { generateBeeswarmChartData } from './charts/correlation/beeswarm/beeswarmOptions'
import { generateScatterChartOptions } from './charts/correlation/scatter/scatterChartOptions'
import useCloseOnOutsideClick from '../common/hooks/useCloseOnOutsideClick'
import { useReactToPrint } from 'react-to-print'
import { toJpeg, toPng, toSvg } from 'html-to-image'
import {
  getBarChartSubtitle,
  getBarChartSubtitleForExport,
  getChartBadges,
  getChartTitle,
} from './charts/common/helper'
import { setDataTableData } from './indicatorProfileSlice'
import ScatterChartWithLogAxis from './charts/correlation/scatter/ScatterChartWithLogAxis'
import { formatUnitMult } from '../common/utils/helper'
import useDeviceIdentification from '../common/hooks/useDeviceIdentification'
import PieChart from './charts/pie/PieChart'
import StackedBarChart from './charts/stackedBar/StackedBarChart'
import { generateStackedBarChartOptions } from './charts/stackedBar/stackedBarChartOptions'
import generatePieChartOptions from './charts/pie/pieChartOptions'

const ChartSection = ({ chartType }) => {
  const {
    selectedCountries,
    selectedCountriesForBar,
    selectedMinYear,
    selectedMaxYear,
    selectedSingleYear,
    selectedFilters,
    indicatorCode,
    disaggregationData,
    disaggregationCountries,
    indicatorMetadata,
    selectedComparison,
    filteredIndicatorData,
    secondIndicatorData,
    selectedSecondIndicator,
    isLoading,
    recentYearSelection,
    selectedUnitMeasure,
    selectedAverage,
    selectedAverages,
    filteredAggregateData,
    selectedFrequency,
    selectedOnlyShow,
    isSecondIndicatorLoading,
    isAggregateDataLoading,
    isDimensionsLoading,
    unitMeasureName,
    secondDisaggregationData,
    countryHierarchy,
    indicatorSpecificRegions,
    countryListForIndicator,
  } = useSelector((state) => state.indicatorProfile)
  const { grouping, featureProps } = useSelector((state) => state.common)
  const [showMenu, setShowMenu] = useState(false)
  const [legendShow, setLegendShow] = useState(0)
  const lineDownloadMenuRef = useRef(null)
  const barDownloadMenuRef = useRef(null)
  const barChartRef = useRef(null)
  useCloseOnOutsideClick(lineDownloadMenuRef, () => setShowMenu(false))
  useCloseOnOutsideClick(barDownloadMenuRef, () => setShowMenu(false))
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const maxLines = 4
  const containerRef = useRef(null)
  const { isTruncated, isExpanded, toggleReadMore } = useShowMore(
    containerRef,
    maxLines
  )
  const isMobile = useDeviceIdentification(Breakpoint.Mobile)

  const { noDataImageURL, site } = featureProps

  const regionCountriesMapping = grouping['HCL_WB_REGIONS']
    ? grouping['HCL_WB_REGIONS']
    : []
  // Get primary regions from H_WB_REGIONS
  const primaryRegions = grouping?.HCL_WB_REGIONS?.map((item) => item.id) || []
  // Get countries that belong to the primary regions from H_REF_ARE_GROUPS.REGION
  const regionHierarchy =
    countryHierarchy?.REGION?.filter((item) =>
      primaryRegions.includes(item.id)
    ) || []
  // Get primary income groups from H_WB_INCOME
  const primaryIncomeGroups =
    grouping?.HCL_WB_INCOME?.map((item) => item.id) || []
  // Get countries that belong to the primary income groups from H_REF_ARE_GROUPS.INCOME
  const incomeGroupHierarchy =
    countryHierarchy?.INCOME?.filter((item) =>
      primaryIncomeGroups.includes(item.id)
    ) || []

  const legendRef = useRef(null)
  const [isLegendReady, setLegendReady] = useState(false)

  useEffect(() => {
    if (legendRef.current) {
      setLegendReady(true)
    } else {
      setLegendReady(false)
    }
  })

  let unitMeasureList = []
  if (
    disaggregationData?.dimensions &&
    disaggregationData?.dimensions.length &&
    !disaggregationData?.dimensions.find(
      (item) => item.field_name === disaggregations.UnitMeasure
    )
  ) {
    const unitMeasureCodes = Array.from(
      new Set(
        [...filteredAggregateData, ...filteredIndicatorData].map(
          (item) => item?.[disaggregations.UnitMeasure]
        )
      )
    )
    const unitMeasureAttributeValues =
      disaggregationData?.attributes?.find(
        (attr) => attr.field_name === disaggregations.UnitMeasure
      )?.field_value || []
    unitMeasureList = unitMeasureCodes.map((item) => {
      return {
        code: item,
        description:
          unitMeasureAttributeValues?.find((uom) => uom.code === item)
            ?.description || '',
        short_label:
          unitMeasureAttributeValues?.find((uom) => uom.code === item)
            ?.short_label || '',
      }
    })
  }

  useEffect(() => {
    setLegendShow(legendShow + 1)
  }, [filteredIndicatorData])
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        handleExitFullscreen()
      } else {
        // if (chartRef.current) {
        //   chartRef.current.chart.setSize(window.innerWidth, 600)
        // }
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const note = indicatorMetadata?.series_description?.aggregation_method
    ? `${indicatorMetadata.series_description.aggregation_method}
              ${
                indicatorMetadata?.series_description.quality_note
                  ? ' ' + indicatorMetadata?.series_description.quality_note
                  : ''
              }${
                indicatorMetadata?.series_description.limitation
                  ? ' ' + indicatorMetadata?.series_description.limitation
                  : ''
              }${
                indicatorMetadata?.series_description.methodology
                  ? ' ' + indicatorMetadata?.series_description.methodology
                  : ''
              }`
    : ''

  const isSingleSelectionGrouping =
    indicatorMetadata?.series_description?.database_id === 'IFC_GB' ||
    chartType === chartTypes.StackedBar ||
    chartType === chartTypes.Pie

  const generateChartOptions = (legendRef) => {
    switch (chartType) {
      case chartTypes.Correlation:
        if (
          selectedSecondIndicator?.name ===
          CORRELATION_NONE_INDICATOR_VALUE?.name
        ) {
          return generateBeeswarmChartData(
            filteredIndicatorData,
            indicatorMetadata,
            disaggregationCountries,
            selectedCountries,
            selectedSingleYear,
            selectedComparison,
            selectedFilters,
            recentYearSelection,
            disaggregationData,
            filteredAggregateData,
            unitMeasureName,
            site,
            selectedOnlyShow[chartTypes.Correlation],
            regionHierarchy,
            incomeGroupHierarchy
          )
        } else {
          return generateScatterChartOptions(
            filteredIndicatorData,
            secondIndicatorData,
            indicatorMetadata,
            disaggregationCountries,
            selectedCountries,
            selectedSingleYear,
            selectedComparison,
            selectedSecondIndicator,
            selectedFilters,
            disaggregationData,
            filteredAggregateData,
            recentYearSelection,
            unitMeasureName,
            selectedUnitMeasure,
            secondDisaggregationData,
            'correlation_white_box',
            unitMeasureList,
            site,
            selectedOnlyShow[chartTypes.Correlation],
            regionHierarchy,
            incomeGroupHierarchy
          )
        }
        case chartTypes.Bar:
        return generateBarChartOptions(
          filteredIndicatorData,
          indicatorMetadata,
          indicatorCode,
          disaggregationCountries,
          selectedCountriesForBar,
          selectedSingleYear,
          selectedComparison,
          disaggregationData,
          selectedFilters,
          recentYearSelection,
          filteredAggregateData,
          countryHierarchy,
          countryListForIndicator,
          unitMeasureName,
          selectedUnitMeasure,
          formatUnitMult(
            disaggregationData,
            filteredIndicatorData?.[0]?.[disaggregations.UnitMult]
          ),
          !isSingleSelectionGrouping
            ? selectedAverages
            : selectedAverage?.[chartTypes.Bar] &&
                selectedAverage?.[chartTypes.Bar] !== NONE
              ? [selectedAverage?.[chartTypes.Bar]]
              : [],
          selectedMinYear,
          selectedMaxYear,
          false,
          unitMeasureList,
          site,
          isFullScreenMode
        )
      case chartTypes.Trend:
      default:
        return generateLineChartOptions(
          filteredIndicatorData,
          indicatorMetadata,
          indicatorCode,
          disaggregationCountries,
          selectedCountries,
          selectedMinYear,
          selectedMaxYear,
          selectedComparison,
          disaggregationData,
          selectedFilters,
          selectedUnitMeasure,
          filteredAggregateData,
          countryHierarchy,
          selectedFrequency,
          legendRef,
          unitMeasureName,
          isMobile,
          formatUnitMult(
            disaggregationData,
            filteredIndicatorData?.[0]?.[disaggregations.UnitMult]
          ),
          selectedAverages,
          regionCountriesMapping,
          unitMeasureList,
          site
        )
      case chartTypes.Pie:
        return generatePieChartOptions({
          filteredIndicatorData,
          filteredAggregateData,
          indicatorMetadata,
          disaggregationCountries,
          selectedCountries,
          selectedComparison,
          disaggregationData,
          selectedAverage,
        })
      case chartTypes.StackedBar:
        return generateStackedBarChartOptions(
          filteredIndicatorData,
          indicatorMetadata,
          disaggregationData,
          selectedFilters,
          filteredAggregateData,
          countryListForIndicator,
          selectedComparison,
          selectedMaxYear,
          selectedMinYear,
          selectedUnitMeasure,
          unitMeasureName,
          unitMeasureList,
          site
        )
    }
  }

  const handleFullscreen = (ele) => {
    if (!document.fullscreenElement) {
      enterFullscreen(ele)
    } else {
      exitFullscreen()
    }
  }
  const enterFullscreen = (ele) => {
    const elem = document.getElementById(ele)
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      // Firefox
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) {
      // Chrome, Safari, and Opera
      elem.webkitRequestFullscreen()
    }
    setIsFullScreenMode(true)
    // Set the size of the chart manually when entering fullscreen
  }
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      // Firefox
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      // Chrome, Safari, and Opera
      document.webkitExitFullscreen()
    }
  }
  const excludeElements = (node) => {
    const exclusionClasses = ['download-exclude']
    return !exclusionClasses.some((classname) =>
      node.classList?.contains(classname)
    )
  }
  const handlePrint = useReactToPrint({
    content: () => document.getElementById('lineChart_container'),
  })
  const downloadLineChart = (fileName, type) => {
    const node = document.getElementById('lineChart_container')

    if (type === 'png') {
      toPng(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'jpeg') {
      toJpeg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'svg') {
      toSvg(node, { filter: excludeElements, backgroundColor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  const handleExitFullscreen = () => {
    // Your custom logic here, e.g., reflowing the chart
    // if (barChartRef.current) {
    setIsFullScreenMode(false)
    // barChartRef.current.chart.reflow()
    // }
  }

  const getBarChartExportOptions = () => {
    return {
      title: getChartTitle(
        indicatorMetadata?.admin_metadata?.[site]?.visualization
          ?.display_name || indicatorMetadata?.series_description.name
      ),
      subtitle: getBarChartSubtitleForExport({
        selectedCountriesForBar,
        disaggregationCountries,
        recentYearSelection,
        filteredIndicatorData,
        selectedFilters,
        disaggregationData: disaggregationData?.dimensions,
        selectedComparison,
        unitMeasureName,
        selectedMinYear,
        selectedMaxYear,
      }),
    }
  }

  const downloadBarChart = (fileName, type) => {
    if (barChartRef.current) {
      const chart = barChartRef.current.chart

      // Step 1: Get the SVG content
      const chartSVG = chart.getSVG({
        ...getBarChartExportOptions(),
      })

      if (type === 'svg') {
        // Step 2: Handle SVG download
        const blob = new Blob([chartSVG], {
          type: 'image/svg+xml;charset=utf-8',
        })

        // Create a temporary link for downloading the SVG
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName // Specify the file name
        document.body.appendChild(link) // Append link temporarily to the DOM
        link.click() // Trigger the download
        document.body.removeChild(link) // Clean up
      } else if (type === 'png' || type === 'jpg') {
        // Step 3: Convert SVG to Canvas for PNG/JPG download
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        // Set SVG as the image source
        const svgBlob = new Blob([chartSVG], {
          type: 'image/svg+xml;charset=utf-8',
        })
        const url = URL.createObjectURL(svgBlob)
        img.src = url

        img.onload = () => {
          // Set canvas size to match the chart dimensions
          canvas.width = img.width
          canvas.height = img.height

          // Draw the SVG on the canvas
          ctx.drawImage(img, 0, 0)

          // Convert canvas to the desired format and trigger download
          const mimeType = type === 'png' ? 'image/png' : 'image/jpeg'
          canvas.toBlob((blob) => {
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = fileName // Specify the file name
            document.body.appendChild(link) // Append link temporarily to the DOM
            link.click() // Trigger the download
            document.body.removeChild(link) // Clean up
            URL.revokeObjectURL(url) // Free up memory
          }, mimeType)
        }

        img.onerror = (error) => {
          console.error('Error loading SVG for conversion:', error)
          URL.revokeObjectURL(url) // Free up memory in case of error
        }
      } else {
        console.error(`Unsupported export type: ${type}`)
      }
    } else {
      console.error('Chart reference is not available.')
    }
  }

  const downloadChartAsPDF = async () => {
    if (barChartRef.current) {
      const chart = barChartRef.current.chart

      // Get the SVG content from the chart
      const chartSVG = chart.getSVG(getBarChartExportOptions())

      // Create an <img> element to render the SVG for conversion
      const svgBlob = new Blob([chartSVG], {
        type: 'image/svg+xml;charset=utf-8',
      })
      const svgUrl = URL.createObjectURL(svgBlob)
      const img = new Image()
      img.src = svgUrl

      img.onload = async () => {
        // Dimensions of the image and PDF
        const chartWidth = img.width
        const chartHeight = img.height
        const pageWidth = 595.28 // A4 page width in points (8.27 inches)
        const pageHeight = 841.89 // A4 page height in points (11.69 inches)
        const margin = 20 // Margin in points (e.g., 20pt = ~0.28 inches)
        const contentWidth = pageWidth - margin * 2 // Width available for content
        const scaleFactor = contentWidth / chartWidth // Scale to fit content width
        const scaledHeight = chartHeight * scaleFactor // Height after scaling
        const availableHeight = pageHeight - margin * 2 // Height available per page

        // Create a new PDF document
        const pdfDoc = await PDFDocument.create()

        // Multi-page logic with padding/margins
        const pagesCount = Math.ceil(scaledHeight / availableHeight)

        for (let i = 0; i < pagesCount; i++) {
          const page = pdfDoc.addPage([pageWidth, pageHeight])
          const canvas = document.createElement('canvas')
          canvas.width = chartWidth
          canvas.height = availableHeight / scaleFactor // Height corresponding to one page
          const ctx = canvas.getContext('2d')

          // Calculate the portion of the chart to draw
          ctx.drawImage(
            img,
            0,
            i * (availableHeight / scaleFactor), // Starting Y position in the source image
            chartWidth,
            availableHeight / scaleFactor, // Source height for the page
            0,
            0,
            chartWidth,
            availableHeight / scaleFactor // Destination height
          )

          // Embed the PNG into the PDF
          const pngImage = await pdfDoc.embedPng(canvas.toDataURL('image/png'))
          page.drawImage(pngImage, {
            x: margin, // Add left margin
            y: pageHeight - availableHeight - margin, // Add top margin
            width: contentWidth,
            height: availableHeight,
          })
        }

        // Save the PDF to a Blob
        const pdfBytes = await pdfDoc.save()

        // Trigger the download
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `${indicatorCode}_bar.pdf` // Specify the file name
        document.body.appendChild(link) // Append link temporarily to the DOM
        link.click() // Trigger the download
        document.body.removeChild(link) // Clean up
      }

      img.onerror = (err) => {
        console.error('Error rendering SVG to image:', err)
      }
    } else {
      console.error('Chart reference is not available.')
    }
  }

  const axisValues =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.axis_values || ''
  const axisValuesParams =
    indicatorMetadata?.admin_metadata?.[site]?.visualization
      ?.axis_values_params || ''

  const linechartData =
    chartType === chartTypes.Trend &&
    !(isSingleSelectionGrouping
      ? selectedComparison !== NONE &&
        selectedCountries.length === 0 &&
        (!selectedAverage[chartType] || selectedAverage[chartType] === NONE)
      : selectedComparison !== NONE &&
        selectedCountries.length === 0 &&
        (!selectedAverages || selectedAverages.length === 0)) &&
    (filteredIndicatorData.length > 0 || filteredAggregateData.length > 0)
      ? generateChartOptions(legendRef)
      : {
          ...LINE_CHART_OPTIONS,
          series: [],
          xAxis: {
            ...LINE_CHART_OPTIONS.xAxis,
            categories: [],
          },
        }
  return (
    <div className="embed-full-row-white-box" id={`${chartType}_white_box`}>
      {chartType === chartTypes.Trend &&
        (isLoading || isAggregateDataLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
           <div
            id="trend_chart_outer_container"
            className="tui_comparison_indicator_container tui_chart_dropdown"
            style={
              isFullScreenMode
                ? {
                    width: '100%',
                    height: '100vh',
                    overflow: 'auto',
                    backgroundColor: 'white',
                  }
                : {}
            }
          >
            {legendShow && (
              <div
                className="button-container download-exclude"
                ref={lineDownloadMenuRef}
              >
                {linechartData?.series?.length > 0 ||
                (linechartData?.series?.length &&
                  linechartData?.series?.every(
                    (item) => item?.data?.length > 0
                  )) ? (
                  <div
                    className="download-icon"
                    onClick={() => {
                      setShowMenu((prevState) => !prevState)
                    }}
                    role="button"
                    tabIndex="0"
                    aria-haspopup="true"
                    aria-expanded={showMenu ? 'true' : 'false'}
                    id="lineDownloadMenu"
                  >
                    <span className="lp lp-bars"></span>
                  </div>
                ) : null}
                <div
                  className={`download-options download-exclude ${
                    showMenu ? 'show' : 'hide'
                  }`}
                  aria-labelledby="lineDownloadMenu"
                >
                  <ul>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        handleFullscreen('trend_chart_outer_container')
                      }}
                    >
                      {isFullScreenMode
                        ? 'Exit from full screen'
                        : 'View in full screen'}
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_trend.png`
                        downloadLineChart(fileName, 'png')
                      }}
                    >
                      Download PNG image
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_trend.jpg`
                        downloadLineChart(fileName, 'jpeg')
                      }}
                    >
                      Download JPEG image
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_trend.svg`
                        downloadLineChart(fileName, 'svg')
                      }}
                    >
                      Download SVG
                    </li>
                    <li
                      onClick={() => {
                        handlePrint()
                        setShowMenu(false)
                      }}
                    >
                      Print
                    </li>          
                  </ul>
                </div>
              </div>
            )}
            <div
              id="lineChart_container"
            >
              {isLegendReady && (
                <LineChart
                  chartKey={`${chartType}-${selectedCountries?.join(
                    ','
                  )}-${selectedComparison}-${selectedMinYear}-${selectedMaxYear}-${selectedUnitMeasure}-${selectedFrequency}-${JSON.stringify(
                    selectedFilters
                  )}`}
                  chartOptions={linechartData}
                  noDataImageURL={noDataImageURL}
                  selectedCountries={selectedCountries}
                  selectedAverages={selectedAverages}
                  chartType={chartType}
                  setLegendRefFalse={(value) => {
                    value && (legendRef.current.innerHTML = '')
                  }}
                />
              )}

              <div ref={legendRef}></div>
            </div>
          </div>
        ))}
      {chartType === chartTypes.Map &&
        (isLoading || isAggregateDataLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
          <WorldMapWrapper
            selectedCountries={selectedCountries}
            unitMeasureList={unitMeasureList}
          />
        ))}

      {chartType === chartTypes.Bar &&
        (isLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
          <div
            id="bar_chart_container"
            style={
              isFullScreenMode
                ? {
                    width: '100%',
                    height: '100vh',
                    overflow: 'auto',
                    backgroundColor: 'white',
                  }
                : {}
            }
          >
            <div className="tui_comparison_indicator_container tui_chart_dropdown">
              <div className="tui_comparison_title_section">
                <div className="tui_heading_h2_sm">
                  <h2>
                    {indicatorMetadata?.admin_metadata?.[site]?.visualization
                      ?.display_name ||
                      indicatorMetadata?.series_description.name}
                  </h2>
                </div>
                <p className="tui__text_base">
                  {getBarChartSubtitle({
                    selectedCountriesForBar,
                    disaggregationCountries,
                    selectedSingleYear,
                    recentYearSelection,
                    filteredIndicatorData,
                    selectedMinYear,
                    selectedMaxYear,
                  })}
                </p>

                <div className="tui__badge_container">
                  <div className="tui_badge_group">
                    {getChartBadges(
                      selectedFilters,
                      disaggregationData,
                      selectedComparison,
                      unitMeasureName
                    )}
                  </div>
                </div>
              </div>
              <div
                ref={barDownloadMenuRef}
                className="button-container download-exclude"
              >
                <div
                  className="download-icon"
                  onClick={() => {
                    setShowMenu((prevState) => !prevState)
                  }}
                  role="button"
                  tabIndex="0"
                  aria-haspopup="true"
                  aria-expanded={showMenu ? 'true' : 'false'}
                  id="barDownloadMenu"
                >
                  <span className="lp lp-bars"></span>
                </div>
                <div
                  className={`download-options download-exclude ${
                    showMenu ? 'show' : 'hide'
                  }`}
                  aria-labelledby="barDownloadMenu"
                >
                  <ul>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        handleFullscreen('bar_chart_container')
                      }}
                    >
                      {isFullScreenMode
                        ? 'Exit from full screen'
                        : 'View in full screen'}
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_bar.png`
                        downloadBarChart(fileName, 'png')
                      }}
                    >
                      Download PNG image
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_bar.jpg`
                        downloadBarChart(fileName, 'jpg')
                      }}
                    >
                      Download JPEG image
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        const fileName = `${indicatorCode}_bar.svg`
                        downloadBarChart(fileName, 'svg')
                      }}
                    >
                      Download SVG
                    </li>
                    <li
                      onClick={() => {
                        setShowMenu(false)
                        downloadChartAsPDF()
                      }}
                    >
                      Print
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <BarChart
              chartRef={barChartRef}
              chartType={chartType}
              chartOptions={
                // show the chart with N.A if none of the countries have the data for selected year
                disaggregationData?.dimensions.length > 0
                  ? // ||
                    // filteredAggregateData.length > 0
                    generateChartOptions()
                  : {
                      ...BAR_CHART_OPTIONS,
                      series: [],
                      xAxis: { ...BAR_CHART_OPTIONS.xAxis, categories: [] },
                    }
              }
              chartKey={`${chartType}-${selectedCountriesForBar?.join(
                ','
              )}-${selectedComparison}-${recentYearSelection}-${selectedMinYear}-${selectedMaxYear}-${
                isSingleSelectionGrouping
                  ? selectedAverage?.[chartTypes.Bar]
                  : selectedAverages.join(',')
              }-${JSON.stringify(filteredAggregateData)}
              -${selectedUnitMeasure}-${selectedFrequency}-${JSON.stringify(
                selectedFilters
              )}`}
              noDataImageURL={noDataImageURL}
            />
          </div>
        ))}
      {chartType === chartTypes.Correlation &&
        (isLoading ||
        isDimensionsLoading ||
        (selectedSecondIndicator?.name !==
          CORRELATION_NONE_INDICATOR_VALUE?.name &&
          isSecondIndicatorLoading) ? (
          <ChartCardContentLoader />
        ) : selectedSecondIndicator?.name ===
          CORRELATION_NONE_INDICATOR_VALUE?.name ? (
          <Beeswarm
            beeswarmData={generateChartOptions()}
            indicatorCode={indicatorCode}
            unitMeasure={selectedUnitMeasure}
            unitMeasureName={unitMeasureName}
            noDataImageURL={noDataImageURL}
            disaggregationData={disaggregationData}
            unitMeasureList={unitMeasureList}
            site={site}
            indicatorSpecificRegions={indicatorSpecificRegions}
            selectedOnlyShow={selectedOnlyShow[chartTypes.Correlation]}
          />
        ) : (
          <>
            {(selectedSecondIndicator?.visualization?.axis_values &&
              (selectedSecondIndicator.visualization.axis_values ===
                AxisValues.Logarithmic ||
                selectedSecondIndicator.visualization.axis_values ===
                  AxisValues.Exponential)) ||
            (axisValues &&
              (axisValues === AxisValues.Logarithmic ||
                axisValues === AxisValues.Exponential)) ? (
              <ScatterChartWithLogAxis
                chartOptions={
                  filteredIndicatorData.length > 0 ||
                  filteredAggregateData.length > 0
                    ? generateChartOptions()
                    : { series: null }
                }
                chartKey={`${chartType}-${selectedCountries?.join(
                  ','
                )}-${selectedComparison}-${selectedSingleYear}-${
                  selectedOnlyShow[chartTypes.Correlation]
                }-${selectedUnitMeasure}-${selectedFrequency}-${JSON.stringify(
                  selectedFilters
                )}`}
                noDataImageURL={noDataImageURL}
                axisValues={axisValues}
                axisValuesParams={axisValuesParams}
                secondIndicatorAxisValues={
                  selectedSecondIndicator?.visualization?.axis_values
                }
                secondIndicatorAxisValuesParams={
                  selectedSecondIndicator?.visualization?.axis_values_params
                }
                decimals={
                  filteredIndicatorData?.[0]?.[disaggregations.Decimals]
                }
                secondIndicatorDecimals={
                  secondIndicatorData?.[0]?.[disaggregations.Decimals]
                }
                datasetID={indicatorMetadata?.series_description?.database_id}
                chartType={chartType}
                indicatorSpecificRegions={indicatorSpecificRegions}
                selectedOnlyShow={selectedOnlyShow[chartTypes.Correlation]}
              />
            ) : (
              <ScatterChart
                chartOptions={
                  filteredIndicatorData.length > 0 ||
                  filteredAggregateData.length > 0
                    ? generateChartOptions()
                    : SCATTER_CHART_OPTIONS
                }
                chartKey={`${chartType}-${selectedCountries?.join(
                  ','
                )}-${selectedComparison}-${selectedSingleYear}-${
                  selectedOnlyShow[chartTypes.Correlation]
                }-${selectedUnitMeasure}-${selectedFrequency}-${JSON.stringify(
                  selectedFilters
                )}`}
                noDataImageURL={noDataImageURL}
                datasetID={indicatorMetadata?.series_description?.database_id}
                indicatorCode={indicatorCode}
                indicatorSpecificRegions={indicatorSpecificRegions}
                selectedOnlyShow={selectedOnlyShow[chartTypes.Correlation]}
              />
            )}
          </>
        ))}
      {chartType === chartTypes.DataTable &&
        (isLoading || isAggregateDataLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
          <div className="data-graph" id="datatable_container">
            <DataTable
              filteredIndicatorData={filteredIndicatorData}
              selectedCountries={selectedCountries}
              countriesData={disaggregationCountries}
              minYear={selectedMinYear}
              maxYear={selectedMaxYear}
              selectedComparison={selectedComparison}
              disaggregationData={disaggregationData}
              countryList={countryListForIndicator}
              selectedFilters={selectedFilters}
              currentYear={selectedSingleYear}
              recentYearSelection={recentYearSelection}
              indicatorName={
                indicatorMetadata?.admin_metadata?.[site]?.visualization
                  ?.display_name || indicatorMetadata?.series_description.name
              }
              setDataTableData={setDataTableData}
              filteredAggregateData={filteredAggregateData}
              noDataImageURL={noDataImageURL}
              selectedFrequency={selectedFrequency}
              selectedUnitMeasure={selectedUnitMeasure}
              unitMeasureName={unitMeasureName}
              selectedAverages={selectedAverages}
              selectedAverage={selectedAverage[chartTypes.DataTable]}
              countryHierarchy={countryHierarchy}
              isSingleSelectionGrouping={isSingleSelectionGrouping}
              disaggregationCountries={disaggregationCountries}
              unitMeasureList={unitMeasureList}
            />
          </div>
        ))}
      {chartType === chartTypes.Pie &&
        (isLoading || isAggregateDataLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
          <div id="pie_chart_container">
            {selectedCountries.length === 0 &&
            selectedAverage[chartType] === NONE ? (
              <div className="no-data-msg no-data-text">
                <p>Select a country or economy to view data</p>
              </div>
            ) : (
              <PieChart
                chartKey={`${chartType}-${selectedCountries?.join(
                  ','
                )}-${selectedComparison}-${selectedSingleYear}-${selectedUnitMeasure}-${JSON.stringify(
                  selectedFilters
                )}`}
                chartOptions={generateChartOptions()}
                countriesData={disaggregationCountries}
                indicatorDataForChart={filteredIndicatorData}
                noDataImageURL={noDataImageURL}
                selectedCountries={selectedCountries}
                selectedAverage={selectedAverage}
                chartType={chartType}
                unitMeasureName={unitMeasureName}
                selectedUnitMeasure={selectedUnitMeasure}
                indicatorMetadata={indicatorMetadata}
                selectedSingleYear={selectedSingleYear}
                setLegendRefFalse={() => {}}
                selectedFilters={selectedFilters}
                disaggregationData={disaggregationData}
                selectedComparison={selectedComparison}
                unitMeasureList={unitMeasureList}
                site={site}
              />
            )}
          </div>
        ))}
      {chartType === chartTypes.StackedBar &&
        (isLoading || isAggregateDataLoading || isDimensionsLoading ? (
          <ChartCardContentLoader />
        ) : (
          <div id="stacked_bar_chart_container">
            {selectedCountries.length === 0 &&
            selectedAverage[chartType] === NONE ? (
              <div className="no-data-msg no-data-text">
                <p>Select a country or economy to view data</p>
              </div>
            ) : (
              <StackedBarChart
                chartType={chartType}
                chartOptions={generateChartOptions()}
                chartKey={`${chartType}-${selectedCountries?.join(
                  ','
                )}-${selectedComparison}-${selectedSingleYear}-${
                  selectedAverage[chartTypes.StackedBar]
                }-${JSON.stringify(
                  filteredAggregateData
                )}-${selectedUnitMeasure}-${selectedFrequency}-${JSON.stringify(
                  selectedFilters
                )}`}
                noDataImageURL={noDataImageURL}
              />
            )}
          </div>
        ))}
      {((isSingleSelectionGrouping &&
        selectedAverage[chartType] &&
        selectedAverage[chartType] !== NONE) ||
        (!isSingleSelectionGrouping &&
          selectedAverages &&
          selectedAverages.length > 0)) &&
        note && (
          <div className="tui__data_source tui_text_base">
            <p
              ref={containerRef}
              style={{
                maxHeight: isExpanded ? 'none' : `${maxLines * 1.5}rem`,
                overflow: 'hidden',
              }}
            >
              <strong>Note: </strong>
              {note}
            </p>
            {isTruncated && (
              <a onClick={toggleReadMore} href="#">
                {isExpanded ? 'Show less' : 'Show more'}
              </a>
            )}
          </div>
        )}
    </div>
  )
}
export default ChartSection
